import { useMutation, useQuery } from "@apollo/client";
import { useMemo } from "react";
import type { CalendarWorker } from "@/domains/appointment/appointments/types/appointment";
import { CREATE_WORKER_MUTATION, UPDATE_WORKER_MUTATION, WORKERS_QUERY } from "@/graphql/workers";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";

interface UseCurrentUserWorkerReturn {
  workerProfile: CalendarWorker | null;
  hasWorkerProfile: boolean;
  loading: boolean;
  error: any;
  createWorkerProfile: (workerData: any) => Promise<void>;
  updateWorkerProfile: (workerData: any) => Promise<void>;
  refetch: () => void;
}

export const useCurrentUserWorker = (): UseCurrentUserWorkerReturn => {
  const { user } = useAuthStore();
  const { currentSalon } = useSalonStore();

  // Query all workers to find current user's worker profile
  const {
    data: workersData,
    loading: workersLoading,
    error: workersError,
    refetch: refetchWorkers,
  } = useQuery(WORKERS_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
      filters: {
        page: 1,
        limit: 100,
        isActive: true,
      },
    },
    skip: !user?.id || !currentSalon?.id,
    pollInterval: 60000,
    errorPolicy: "all",
  });

  // Create worker mutation
  const [createWorker, { loading: createLoading }] = useMutation(CREATE_WORKER_MUTATION, {
    onCompleted: () => {
      refetchWorkers();
    },
    onError: (error) => {
      console.error("Error creating worker profile:", error);
    },
  });

  // Update worker mutation
  const [updateWorker, { loading: updateLoading }] = useMutation(UPDATE_WORKER_MUTATION, {
    onCompleted: () => {
      refetchWorkers();
    },
    onError: (error) => {
      console.error("Error updating worker profile:", error);
    },
  });

  // Find current user's worker profile
  const workerProfile = useMemo(() => {
    if (!workersData?.workers?.workers || !user?.id) return null;

    const userWorker = workersData.workers.workers.find(
      (worker: any) => worker.user?.id === user.id
    );

    if (!userWorker) return null;

    // Transform to CalendarWorker format
    return {
      id: userWorker.id,
      name: userWorker.displayName,
      email: userWorker.user?.email ?? "",
      role: userWorker.user?.role ?? "",
      bio: userWorker.bio ?? undefined,
      profileImageUrl: userWorker.profileImageUrl ?? undefined,
      color: userWorker.color,
      isActive: userWorker.isActive,
      canAcceptOnlineBookings: userWorker.canAcceptOnlineBookings,
      specialties: [],
      availability: {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null,
      },
      vacations: [],
    } as CalendarWorker;
  }, [workersData, user]);

  const hasWorkerProfile = !!workerProfile;

  const createWorkerProfile = async (workerData: any) => {
    if (!user?.id) {
      throw new Error("No user logged in");
    }
    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    await createWorker({
      variables: {
        salonId: currentSalon.id,
        input: {
          userId: user.id,
          ...workerData,
        },
      },
    });
  };

  const updateWorkerProfile = async (workerData: any) => {
    if (!workerProfile?.id) {
      throw new Error("No worker profile to update");
    }
    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    await updateWorker({
      variables: {
        salonId: currentSalon.id,
        workerId: workerProfile.id,
        input: workerData,
      },
    });
  };

  const loading = workersLoading || createLoading || updateLoading;
  const error = workersError;

  const refetch = () => {
    refetchWorkers();
  };

  return {
    workerProfile,
    hasWorkerProfile,
    loading,
    error,
    createWorkerProfile,
    updateWorkerProfile,
    refetch,
  };
};
